import { useState } from "react";
import { useNotify } from "ra-core";
import { Plus, Trash2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";

import { pruefe, sb } from "./daten";
import type { Posten } from "./kern/rechnen";
import {
  KATEGORIEN,
  PRIVAT,
  cent,
  type Buchung,
  type Sonderfall,
  type Steuerjahr,
  type Teil,
} from "./kern/typen";
import {
  Abschnitt,
  Auswahl,
  Feld,
  Hinweis,
  Tabelle,
  Zahl,
  fmtCent,
  fmtDatum,
  zahlLesen,
  type TabProps,
} from "./teile";

const useSchreiben = (neuLaden: () => Promise<unknown>) => {
  const notify = useNotify();
  return async (
    aktion: () => PromiseLike<{ error: { message: string } | null }>,
  ) => {
    try {
      pruefe(await aktion());
      await neuLaden();
      return true;
    } catch (e) {
      notify(e instanceof Error ? e.message : String(e), { type: "error" });
      return false;
    }
  };
};

const SaetzeAuswahl = ({
  value,
  onChange,
  name,
}: {
  value: number;
  onChange: (s: number) => void;
  name: string;
}) => (
  <Auswahl
    aria-label={name}
    value={value}
    onChange={(e) => onChange(Number(e.target.value))}
  >
    <option value={19}>19 %</option>
    <option value={7}>7 %</option>
    <option value={0}>0 %</option>
  </Auswahl>
);

const KategorieAuswahl = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (k: string) => void;
}) => (
  <Auswahl
    aria-label="Kategorie"
    value={value}
    onChange={(e) => onChange(e.target.value)}
  >
    <option value="">offen</option>
    {KATEGORIEN.map((k) => (
      <option key={k} value={k}>
        {k}
      </option>
    ))}
  </Auswahl>
);

export const RueckfragenTab = ({
  daten,
  basis,
  posten,
  neuLaden,
}: TabProps) => {
  const [alle, setAlle] = useState(false);
  const [nurPrivat, setNurPrivat] = useState(false);
  const [offenId, setOffenId] = useState<number | null>(null);
  const schreiben = useSchreiben(neuLaden);

  const gesehen = new Set<number>();
  const zeilen = posten.filter((p) => {
    if (!p.buchungId || gesehen.has(p.buchungId)) return false;
    if (nurPrivat && !p.kontoPrivat) return false;
    if (!alle && p.kategorie && !p.unsicher.length && !p.duplikat) return false;
    gesehen.add(p.buchungId);
    return true;
  });

  return (
    <div className="space-y-4">
      <Abschnitt
        titel="Rückfragen"
        text="Unbekanntes bleibt ohne Kategorie und zählt nicht mit, das CRM rät nicht."
      >
        <div className="flex flex-wrap gap-4 text-sm">
          <label className="flex items-center gap-2">
            <Switch checked={alle} onCheckedChange={setAlle} /> Auch erledigte
            Zeilen
          </label>
          <label className="flex items-center gap-2">
            <Switch checked={nurPrivat} onCheckedChange={setNurPrivat} /> Nur
            Privatkonten
          </label>
        </div>
        {zeilen.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nichts offen.</p>
        ) : (
          <Tabelle
            kopf={["Datum", "Konto", "Partner / Zweck", "Betrag", "Stand", ""]}
            rechts={[3]}
          >
            {zeilen.map((p) => {
              const b = daten.buchungen.find((x) => x.id === p.buchungId);
              if (!b) return null;
              return (
                <Zeile
                  key={b.id}
                  b={b}
                  p={p}
                  jahr={basis.jahr}
                  offen={offenId === b.id}
                  umschalten={() => setOffenId(offenId === b.id ? null : b.id)}
                  schreiben={schreiben}
                  regelBekannt={daten.regeln.some(
                    (r) => r.muster.toLowerCase() === b.partner.toLowerCase(),
                  )}
                />
              );
            })}
          </Tabelle>
        )}
      </Abschnitt>
      <Regeln {...{ daten, schreiben }} />
    </div>
  );
};

type ZeileProps = {
  b: Buchung;
  p: Posten;
  jahr: Steuerjahr;
  offen: boolean;
  umschalten: () => void;
  schreiben: ReturnType<typeof useSchreiben>;
  regelBekannt: boolean;
};

const Zeile = ({
  b,
  p,
  jahr,
  offen,
  umschalten,
  schreiben,
  regelBekannt,
}: ZeileProps) => (
  <>
    <tr>
      <td className="whitespace-nowrap">{fmtDatum(b.datum)}</td>
      <td>{p.konto}</td>
      <td className="min-w-48">
        <div>{b.partner}</div>
        <div className="text-xs text-muted-foreground">{b.text}</div>
        {p.unsicher.map((u) => (
          <div key={u} className="text-xs text-amber-700 dark:text-amber-300">
            {u}
          </div>
        ))}
        {p.duplikat && (
          <div className="text-xs text-amber-700 dark:text-amber-300">
            Mögliches Duplikat: bei doppeltem Export eine Zeile auf Ignorieren
            setzen
          </div>
        )}
      </td>
      <Zahl>{fmtCent(cent(b.betrag))}</Zahl>
      <td>
        {p.kategorie ? (
          <Badge variant="secondary">{p.kategorie}</Badge>
        ) : (
          <Badge variant="outline">offen</Badge>
        )}
        {b.teile?.length ? (
          <Badge variant="outline" className="ml-1">
            aufgeteilt
          </Badge>
        ) : null}
      </td>
      <td className="text-right whitespace-nowrap">
        {p.kontoPrivat && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              schreiben(() =>
                sb()
                  .from("bh_buchung")
                  .update({
                    kategorie: PRIVAT,
                    satz: 0,
                    anteil: 100,
                    teile: null,
                  })
                  .eq("id", b.id),
              )
            }
          >
            Privat
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={umschalten}>
          {p.kontoPrivat ? "Geschäftlich" : "Entscheiden"}
        </Button>
      </td>
    </tr>
    {offen && (
      <tr>
        <td colSpan={6} className="bg-muted/40">
          <Entscheidung
            b={b}
            p={p}
            jahr={jahr}
            fertig={umschalten}
            schreiben={schreiben}
            regelBekannt={regelBekannt}
          />
        </td>
      </tr>
    )}
  </>
);

const Entscheidung = ({
  b,
  p,
  jahr,
  fertig,
  schreiben,
  regelBekannt,
}: Omit<ZeileProps, "offen" | "umschalten"> & { fertig: () => void }) => {
  const vorschlag = p.vorschlag;
  const [kategorie, setKategorie] = useState(
    b.kategorie ?? vorschlag?.kategorie ?? "",
  );
  const [satz, setSatz] = useState(b.satz ?? vorschlag?.satz ?? 19);
  const [anteil, setAnteil] = useState(b.anteil);
  const [beleg, setBeleg] = useState(b.beleg ?? "");
  const [sonderfall, setSonderfall] = useState<Sonderfall | "">(
    b.sonderfall ?? vorschlag?.sonderfall ?? "",
  );
  const [regel, setRegel] = useState(false);
  const [teile, setTeile] = useState<
    (Omit<Teil, "betrag"> & { betrag: string })[]
  >(
    (b.teile ?? []).map((t) => ({
      ...t,
      betrag: String(t.betrag).replace(".", ","),
    })),
  );
  const summe = teile.reduce(
    (s, t) => s + cent(zahlLesen(t.betrag.replace(/^−/, "-"))),
    0,
  );
  const teileOk = !teile.length || summe === cent(b.betrag);

  const speichern = async () => {
    const aufgeteilt = teile.length > 0;
    const gespeicherteTeile = aufgeteilt
      ? teile.map((t) => ({
          ...t,
          betrag: zahlLesen(t.betrag.replace(/^−/, "-")),
        }))
      : null;
    const ok = await schreiben(() =>
      sb()
        .from("bh_buchung")
        .update({
          kategorie: aufgeteilt
            ? gespeicherteTeile?.[0].kategorie
            : kategorie || null,
          satz: aufgeteilt ? gespeicherteTeile?.[0].satz : satz,
          anteil: aufgeteilt ? 100 : anteil,
          teile: gespeicherteTeile,
          beleg: beleg || null,
          sonderfall: sonderfall || null,
        })
        .eq("id", b.id),
    );
    if (ok && regel && b.partner && kategorie && !regelBekannt) {
      await schreiben(() =>
        sb()
          .from("bh_regel")
          .insert({
            muster: b.partner,
            kategorie,
            satz,
            sonderfall: sonderfall || null,
          }),
      );
    }
    if (ok) fertig();
  };

  return (
    <div className="space-y-3 py-1">
      {vorschlag && p.kontoPrivat && (
        <Hinweis>
          Vorschlag aus Regel „{vorschlag.muster}“: {vorschlag.kategorie},{" "}
          {vorschlag.satz} %
        </Hinweis>
      )}
      {!teile.length && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          <Feld name="Kategorie">
            <KategorieAuswahl value={kategorie} onChange={setKategorie} />
          </Feld>
          <Feld name="Satz">
            <SaetzeAuswahl name="Satz" value={satz} onChange={setSatz} />
          </Feld>
          <Feld name="Geschäftlicher Anteil (%)">
            <Input
              type="number"
              min={0}
              max={100}
              value={anteil}
              onChange={(e) => setAnteil(Number(e.target.value))}
            />
          </Feld>
          <div className="flex items-end gap-1 pb-0.5">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setAnteil(jahr.anteil_telefon)}
            >
              Telefon {jahr.anteil_telefon} %
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setAnteil(jahr.anteil_kabel)}
            >
              Kabel {jahr.anteil_kabel} %
            </Button>
          </div>
        </div>
      )}
      {teile.map((t, i) => (
        <div
          key={i}
          className="grid items-end gap-2 sm:grid-cols-[1fr_2fr_1fr_auto]"
        >
          <Feld name="Teilbetrag brutto">
            <Input
              inputMode="decimal"
              value={t.betrag}
              onChange={(e) =>
                setTeile(
                  teile.map((x, j) =>
                    j === i ? { ...x, betrag: e.target.value } : x,
                  ),
                )
              }
            />
          </Feld>
          <Feld name="Kategorie">
            <KategorieAuswahl
              value={t.kategorie}
              onChange={(k) =>
                setTeile(
                  teile.map((x, j) => (j === i ? { ...x, kategorie: k } : x)),
                )
              }
            />
          </Feld>
          <Feld name="Satz">
            <SaetzeAuswahl
              name={`Satz Teil ${i + 1}`}
              value={t.satz}
              onChange={(s) =>
                setTeile(teile.map((x, j) => (j === i ? { ...x, satz: s } : x)))
              }
            />
          </Feld>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Teil entfernen"
            onClick={() => setTeile(teile.filter((_, j) => j !== i))}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      {teile.length > 0 && !teileOk && (
        <Hinweis art="warnung">
          Teilbeträge ergeben {fmtCent(summe)}, die Buchung hat{" "}
          {fmtCent(cent(b.betrag))}. Sie tragen das Vorzeichen der Buchung.
        </Hinweis>
      )}
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
        <Feld name="Beleg">
          <Input value={beleg} onChange={(e) => setBeleg(e.target.value)} />
        </Feld>
        <Feld name="Sonderfall">
          <Auswahl
            value={sonderfall}
            onChange={(e) => setSonderfall(e.target.value as Sonderfall | "")}
          >
            <option value="">keiner</option>
            <option value="13b">§ 13b Steuerschuldumkehr</option>
            <option value="ige">Innergemeinschaftlicher Erwerb</option>
          </Auswahl>
        </Feld>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" disabled={!teileOk} onClick={speichern}>
          Speichern
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() =>
            setTeile([
              ...teile,
              ...(teile.length
                ? []
                : [{ betrag: "", kategorie: kategorie, satz: 7 }]),
              { betrag: "", kategorie: kategorie, satz: 19 },
            ])
          }
        >
          <Plus className="h-4 w-4" /> Aufteilen 7/19 %
        </Button>
        {b.partner && !regelBekannt && !teile.length && (
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={regel} onCheckedChange={setRegel} /> Als Regel
            merken
          </label>
        )}
      </div>
    </div>
  );
};

const Regeln = ({
  daten,
  schreiben,
}: {
  daten: TabProps["daten"];
  schreiben: ReturnType<typeof useSchreiben>;
}) => {
  const [f, setF] = useState({ muster: "", kategorie: "", satz: 19 });
  return (
    <Abschnitt
      titel="Regeln"
      text="Muster in Partner oder Zweck ergibt Kategorie und Satz. Die erste passende Regel gilt."
    >
      <Tabelle kopf={["Muster", "Kategorie", "Satz", "Hinweis", ""]}>
        {daten.regeln.map((r) => (
          <tr key={r.id}>
            <td>{r.muster}</td>
            <td>{r.kategorie}</td>
            <td className="whitespace-nowrap">{r.satz} %</td>
            <td className="text-xs text-muted-foreground">{r.hinweis}</td>
            <td className="text-right">
              <Button
                variant="ghost"
                size="icon"
                aria-label="Regel löschen"
                onClick={() =>
                  schreiben(() => sb().from("bh_regel").delete().eq("id", r.id))
                }
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </td>
          </tr>
        ))}
      </Tabelle>
      <div className="flex flex-wrap items-end gap-2">
        <Feld name="Muster">
          <Input
            value={f.muster}
            onChange={(e) => setF({ ...f, muster: e.target.value })}
          />
        </Feld>
        <Feld name="Kategorie" className="min-w-52">
          <KategorieAuswahl
            value={f.kategorie}
            onChange={(k) => setF({ ...f, kategorie: k })}
          />
        </Feld>
        <Feld name="Satz">
          <SaetzeAuswahl
            name="Satz Regel"
            value={f.satz}
            onChange={(s) => setF({ ...f, satz: s })}
          />
        </Feld>
        <Button
          variant="outline"
          disabled={!f.muster || !f.kategorie}
          onClick={async () => {
            if (await schreiben(() => sb().from("bh_regel").insert(f)))
              setF({ muster: "", kategorie: "", satz: 19 });
          }}
        >
          Regel anlegen
        </Button>
      </div>
    </Abschnitt>
  );
};
